import React, { useEffect, useState } from "react";
import DataTable from "../DataTable";
import {
  getEventsByUserId,
  getCurrentUserDetails,
  getAllEvents,
  getUserByID,
  getListingByUserID,
  getBookingsByListingId,
  getListingByID, // <-- Add this import
  formatBookingStatusLabel,
} from "../utils";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Row, Col, Spinner } from "react-bootstrap";
import { FaMoneyCheckAlt, FaCalendarAlt } from "react-icons/fa";
import { getPassengerCounts } from "../utils/passengerCounts";

function getDateOnly(date) {
  if (!date) return null;
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function toSortTime(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getEventBookingStatus(event) {
  return event?.bookingStatus || event?.inquireInfo?.status || "";
}

function isCompletedBookingStatus(status) {
  const normalized = String(status || "").toLowerCase().trim();
  return [
    "trip returned",
    "trip completed",
    "trip concluded",
    "accepted",
  ].includes(normalized);
}

export default function AllEvents() {
  const auth = useAuth();
  const currentUser = auth.currentUser;
  const [events, setEvents] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedReferralCode, setSelectedReferralCode] = useState("");
  const [selectedOwnerNameWithCode, setSelectedOwnerNameWithCode] = useState("");

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const userDetails = await getCurrentUserDetails();
        setUserRole(userDetails.role);
        let fetchedEvents = [];
        if (
          userDetails.role === "Boat Owner" ||
          userDetails.role === "Renter"
        ) {
          fetchedEvents = await getEventsByUserId(currentUser.uid);
        } else if (userDetails.role === "Admin") {
          fetchedEvents = await getAllEvents(currentUser.uid);
        }
        setEvents(fetchedEvents || []);
      } catch (error) {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [currentUser]);

  useEffect(() => {
    const fetchTableData = async () => {
      setTableLoading(true);
      const filteredEvents = events.filter(
        (event) => event.id && event.id !== "undefined"
      );
      console.log(filteredEvents, "filteredEvents");
      const dataPromises = filteredEvents.map(async (event) => {
        const ownerId = event.inquireInfo?.owner;
        const renterId = event.inquireInfo?.renter;
        let ownerName = "-";
        let ownerEmail = "-";
        let renterName = "-";
        let referralCode = "-";
        let isFirstBooking = "-";
        let referralAmount = "-";
        if (ownerId) {
          const owner = await getUserByID(ownerId);
          if (owner && !owner.isDeleted) {
            ownerName = `${owner.firstName || ""} ${
              owner.lastName || ""
            }`.trim();
            ownerEmail = owner.email || "-";
            referralCode = owner.refferalCode || "-";
          } else if (owner?.isDeleted) {
            return null; // Skip events with deleted owners
          }
        }
        if (renterId) {
          const renter = await getUserByID(renterId);
          if (renter && !renter.isDeleted) {
            renterName = `${renter.firstName || ""} ${
              renter.lastName || ""
            }`.trim();
          } else if (renter?.isDeleted) {
            return null; // Skip events with deleted renters
          }
        }
        // 1st Booking and Referral Amount logic (booking status based)
        const listingId = event.inquireInfo?.listingId;
        const eventStatus = getEventBookingStatus(event);
        let boatType = event.inquireInfo?.boatType ?? "-";
        if ((!boatType || boatType === "-") && listingId) {
          const listing = await getListingByID(listingId);
          if (listing && listing.boatType) {
            boatType = listing.boatType;
          }
        }
        const boatName = event.inquireInfo?.boatName ?? event.title ?? "-";

        // Skip Kayak boat types for 1st Booking and Referral Amount calculations
        if (listingId && isCompletedBookingStatus(eventStatus) && boatType !== "Kayak") {
          // Get all events for this listing with a completed status
          const allEventsForListing = events.filter(
            (e) =>
              e.inquireInfo?.listingId === listingId &&
              isCompletedBookingStatus(getEventBookingStatus(e)) &&
              e.inquireInfo?.boatType !== "Kayak"
          );
          allEventsForListing.sort((a, b) => {
            const tA = a.inquireInfo?.timestamp || 0;
            const tB = b.inquireInfo?.timestamp || 0;
            return tA - tB;
          });
          
          // Only show 1st Booking Yes/No if there's a referral code
          if (referralCode && referralCode !== "-") {
            if (
              allEventsForListing.length > 0 &&
              allEventsForListing[0].id === event.id
            ) {
              isFirstBooking = "Yes";
              referralAmount = "$25";
            } else {
              isFirstBooking = "No";
              referralAmount = "$10";
            }
          } else {
            // No referral code - don't show 1st Booking values
            isFirstBooking = "-";
            referralAmount = "-";
          }
        } else {
          isFirstBooking = "-";
          referralAmount = "-";
        }
        const startDate = event.start
          ? new Date(event.start)
          : event.inquireInfo?.offerData?.offeredDate
          ? new Date(event.inquireInfo.offerData.offeredDate)
          : null;
        // If boat type is Kayak, always show '-' for referral code
        const displayReferralCode = boatType === "Kayak" ? "-" : referralCode;
        const inquiryData = event.inquireInfo?.inquiryData;
        const hasPassengerData = Boolean(
          inquiryData &&
            (inquiryData.total != null ||
              inquiryData.adults != null ||
              inquiryData.children != null ||
              inquiryData.minors != null)
        );
        return {
          id: event.id,
          title: event.title,
          referralCode: displayReferralCode,
          start: event.start ? new Date(event.start).toLocaleString() : "-",
          end: event.end ? new Date(event.end).toLocaleString() : "-",
          offerPrice: event.inquireInfo?.offerData?.offerPrice ?? "-",
          duration: event.inquireInfo?.offerData?.duration ?? "-",
          guests: hasPassengerData
            ? getPassengerCounts(inquiryData).total
            : "-",
          status: formatBookingStatusLabel(getEventBookingStatus(event)),
          color: event.color,
          boatName,
          boatType,
          ownerName,
          ownerEmail,
          renterName,
          "1stBooking": isFirstBooking,
          referralAmount,
          _createdAt: toSortTime(
            event.createdAt || event.inquireInfo?.timestamp
          ),
          _sortDate: startDate ? startDate.getTime() : 0,
          _dateOnly: startDate ? getDateOnly(startDate).getTime() : 0,
        };
      });
      const resolvedData = (await Promise.all(dataPromises)).filter(Boolean);
      const sortedData = resolvedData.sort((a, b) => {
        if (b._createdAt !== a._createdAt) {
          return b._createdAt - a._createdAt;
        }
        const idCompare = (b.id || "").localeCompare(a.id || "");
        if (idCompare !== 0) {
          return idCompare;
        }
        if (b._dateOnly !== a._dateOnly) {
          return b._dateOnly - a._dateOnly;
        }
        return b._sortDate - a._sortDate;
      });
      setTableData(sortedData);
      setTableLoading(false);
    };
    if (events.length > 0) fetchTableData();
    else setTableData([]);
  }, [events]);

  // Extract unique referral codes (excluding '-')
  const referralCodes = Array.from(
    new Set(tableData.map(event => event.referralCode).filter(code => code && code !== "-"))
  );
  // Filter table data based on selected referral code
  const filteredTableData = selectedReferralCode
    ? tableData.filter(event => event.referralCode === selectedReferralCode)
    : tableData;

  // Extract unique owner name/referral code pairs for dropdown
  const ownerNamesWithCodes = Array.from(
    new Map(
      tableData
        .filter(event => event.ownerName && event.referralCode && event.referralCode !== "-")
        .map(event => [event.referralCode, { ownerName: event.ownerName, referralCode: event.referralCode }])
    ).values()
  );
  // Filter table data based on selected owner name/referral code
  const filteredTableDataByOwner = selectedOwnerNameWithCode
    ? filteredTableData.filter(event => event.referralCode === selectedOwnerNameWithCode)
    : filteredTableData;

  // Check if user should see referral columns (Boat Owner or Admin)
  const shouldShowReferralColumns = userRole === "Boat Owner" || userRole === "Admin";
  
  // Check if there are any events with valid referral codes
  const hasValidReferralCodes = tableData.some(event => 
    event.referralCode && event.referralCode !== "-" && event.referralCode !== null
  );

  const handleRowClick = (row) => {
    const eventId = row?.original?.id;
    if (eventId && eventId !== "undefined") {
      navigate(`/dashboard/event/${eventId}`);
    }
  };

  const columns = [
    { Header: "Event ID", accessor: "id" },
    { Header: "Title", accessor: "title" },
    { Header: "Boat Name", accessor: "boatName" },
    { Header: "Boat Type", accessor: "boatType" },
    { Header: "Renter Name", accessor: "renterName" },
    { Header: "Boat Owner Name", accessor: "ownerName" },
    // Conditionally show referral columns
    ...(shouldShowReferralColumns && hasValidReferralCodes ? [
      { Header: "Referral Code", accessor: "referralCode" },
      { Header: "1st Booking", accessor: "1stBooking" },
      { Header: "Referral Amount", accessor: "referralAmount" }
    ] : []),
    { Header: "Start", accessor: "start" },
    { Header: "End", accessor: "end" },
    {
      Header: "Offered Price",
      accessor: "offerPrice",
      Cell: ({ value }) => (value && value !== "-" ? `$${value}` : "-"),
    },
    { Header: "Duration", accessor: "duration" },
    { Header: "Guests", accessor: "guests" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => {
        const status = row.original.status;
        const color = row.original.color;
        return (
          <span
            style={{
              color: color,
              fontWeight: 500,
              fontSize: 14,
              textTransform: "capitalize",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      Header: "View",
      id: "view",
      Cell: ({ row }) => (
        <button
          className="btn btn-primary btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            if (row.original.id && row.original.id !== "undefined") {
              navigate(`/dashboard/event/${row.original.id}`);
            }
          }}
          disabled={!row.original.id || row.original.id === "undefined"}
        >
          View
        </button>
      ),
    },
  ];

  const totalEvents = tableData.length;
  const totalOfferedPrice = tableData.reduce((sum, event) => {
    const price = parseFloat(
      event.offerPrice && event.offerPrice !== "-" ? event.offerPrice : 0
    );
    return sum + (isNaN(price) ? 0 : price);
  }, 0);

  useEffect(() => {
    const checkRentedBoats = async () => {
      try {
        const ownerId = currentUser.uid;
        const listings = await getListingByUserID(ownerId);

        // Check if listings is an array and not empty
        if (listings && Array.isArray(listings) && listings.length > 0) {
          for (const listing of listings) {
            const bookings = await getBookingsByListingId(listing.id);
            const completedBookings = bookings.filter(
              (b) => b.status === "Trip Returned" || b.status === "Trip Completed"
            );
            console.log(
              `Boat ${listing.listingTitle} has been rented ${completedBookings.length} times.`
            );
          }
        } else {
          console.log(
            "No listings found for this user or listings is not an array"
          );
        }
      } catch (error) {
        console.error("Error checking rented boats:", error);
      }
    };
    checkRentedBoats();
  }, [currentUser]);

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>All Bookings</h1>
          </div>
        </div>
      </div>
      <Row>
        <Col xs={12} md={6}>
          <div className="card widget widget-bank-card">
            <div className="card-body">
              <div className="widget-bank-card-container widget-bank-card-mastercard d-flex flex-column">
                <div className="card-logo">
                  <FaCalendarAlt style={{ width: "50px", height: "50px" }} />
                </div>
                <span className="widget-bank-card-balance-title">
                  Total Events
                </span>
                <span className="widget-bank-card-balance">{totalEvents}</span>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="card widget widget-bank-card">
            <div className="card-body">
              <div className="widget-bank-card-container widget-bank-card-visa d-flex flex-column">
                <div className="card-logo">
                  <FaMoneyCheckAlt
                    style={{ width: "50px", height: "50px", color: "white" }}
                  />
                </div>
                <span className="widget-bank-card-balance-title">
                  Total Offered Price
                </span>
                <span className="widget-bank-card-balance">
                  $
                  {totalOfferedPrice.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <div className="row">
        <div className="col-12">
          <div className="card calendar-container">
            <div className="card-body">
              {loading || tableLoading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 200 }}
                >
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                    {console.log(tableData, "tableData")}
                  </Spinner>
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredTableDataByOwner}
                  onRowClick={handleRowClick}
                  referralCodes={referralCodes}
                  selectedReferralCode={selectedReferralCode}
                  onReferralCodeChange={setSelectedReferralCode}
                  ownerNamesWithCodes={ownerNamesWithCodes}
                  selectedOwnerNameWithCode={selectedOwnerNameWithCode}
                  onOwnerNameWithCodeChange={setSelectedOwnerNameWithCode}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
